import { DEFAULT_SSR_COOKIE_KEY } from '../constants';
import { type ReadonlyCookieStore, type AuthSession } from './types';

/**
 * Read the current auth session from cookies.
 *
 * Because the SSR cookie is written with `max-age` matching the token
 * lifetime, the browser automatically removes it once the token expires.
 * If the cookie is present the token is considered valid.
 *
 * Works in Server Components, Server Actions, Route Handlers, and Middleware.
 *
 * @example
 * ```ts
 * import { cookies } from 'next/headers';
 * import { getSession } from '@docyrus/signin/nextjs';
 * import { redirect } from 'next/navigation';
 *
 * export default async function Page() {
 *   const session = getSession(await cookies());
 *   if (!session.isAuthenticated) redirect('/login');
 *   // ...
 * }
 * ```
 */
export function getSession(
  cookieStore: ReadonlyCookieStore,
  cookieKey?: string
): AuthSession {
  const key = cookieKey ?? DEFAULT_SSR_COOKIE_KEY;
  const tokenCookie = cookieStore.get(key);

  const accessToken = tokenCookie?.value ? decodeURIComponent(tokenCookie.value) : null;

  return {
    accessToken,
    isAuthenticated: accessToken !== null
  };
}
